import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { useTheme } from "react-jss";
import useStyles from "./SecurityQuestionRendered.styles";
import { ThemeModel } from "../../../models/ThemeModel";
import { translate, translatedSecurityQuestionsList } from "../../../models/utilities/translationUtilities";
import { QuestionnaireModel } from "../../../models/QuestionnaireModel";
import { useSelector } from "react-redux";
import { StoreState } from "../../../models/states/StoreState";
import { RecalledQuestionLabel } from "./QuestionRenderer";
import { RecalledText } from "../../../containers/shared/RecalledText";
import Select, { Option } from "../../select/Select";
import InputBox from "../../shared/InputBox";
import { NavigationType } from "../../../models/enums/NavigationType";
import ValidationError from "../../shared/ValidationError";
import { ValidationErrorModel } from "../../../models/ValidationErrorModel";
import { QuestionModel } from "../../../models/QuestionModel";
import { QuestionType } from "../../../models/enums/QuestionType";
import useDimensionContext from "../../../hooks/useDimensionContext";
import classNames from "classnames";
import { useThunkDispatch } from "../../../redux/thunks";
import { saveSecurityQuestionInfo } from "../../../redux/thunks/securityQuestion";
import { VariableDataType } from "../../../models/enums/VariableDataType";
import { VariableType } from "../../../models/enums/VariableType";
import { useKeyboardNavigation } from "../../../hooks/useKeyboardNavigation";
import { TextAnswerFieldNavigation } from "../../controls/KeyboardNavigation/TextAnswerFieldNavigation";
import { useModernSecurityQuestionStyles } from "../../../theme/securityQuestionThemeTokens";
import type { Theme } from "../../../types/Theme";

interface Props {
    theme: ThemeModel;
    questionnaire: QuestionnaireModel;
    language: string;
    onNavigation: (type: NavigationType) => void;
    isResLocked: boolean;
    onLockedChange?: (isLocked: boolean) => void;
}

interface ValidationState {
    questionError?: ValidationErrorModel;
    answerError?: ValidationErrorModel;
}

export interface SecurityQuestionModel {
    id: number,
    questionId: number,
    questionText: string;
}

// Extracted reusable components
const QuestionText = memo(({ text, isIOS }: { text: string; isIOS: boolean }) =>
    isIOS ? <RecalledQuestionLabel text={text} /> : <RecalledText text={text} />
);

const SecurityQuestionInput = memo(({
    answer,
    onChange,
    onFocus,
    onBlur,
    focused,
    hasError,
    className,
    elementReference,
    onKeyDown,
    submitSecurityQuestionForm
}: {
    answer: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
    focused: boolean;
    hasError: boolean;
    className: string;
    elementReference: React.RefObject<HTMLInputElement>;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    submitSecurityQuestionForm: () => void;
}) => (
    <InputBox
        focused={focused}
        freeform={false}
        size="Medium"
        className={className}
    >
        <input
            type="text"
            value={answer}
            onChange={onChange}
            aria-invalid={hasError}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={(event) => {
                if (event.key !== " " && event.key !== "Enter") {
                    onKeyDown(event);
                } else if (event.key === "Enter") {
                    // Handle space/enter ourselves
                    submitSecurityQuestionForm();
                    event.preventDefault();
                }
            }}
            ref={elementReference}
        />
    </InputBox>
));

export default function SecurityQuestionRenderer(props: Props) {
    const { questionnaire, language, onNavigation, isResLocked, onLockedChange } = props;
    const theme = useTheme<Theme>();
    const classes = useStyles({ theme });
    const useModernTheme = useModernSecurityQuestionStyles(theme);
    const dispatch = useThunkDispatch();
    const mountedRef = React.useRef(true);
    const resSelectedSecurityQuestionId = useSelector((state: StoreState) => state.interview.respondent.resSelectedSecurityQuestionId);
    const [resSelectedQuestionText, setResSelectedQuestionText] = useState("");
    const [selectedQuestion, setSelectedQuestion] = useState("");
    const [answer, setAnswer] = useState("");
    const [validationState, setValidationState] = useState<ValidationState>({});
    const [focused, setFocused] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLocked, setIsLocked] = useState(false);
    const isReAccess = resSelectedSecurityQuestionId != 0;

    const dimension = useDimensionContext();
    const isDisplayWide = useMemo(() => dimension?.minWidth(778) ?? false, [dimension]);

    const device = useSelector((state: StoreState) => state.interview.device);
    const isIOS = useMemo(() =>
        ["ios", "mac_os_x", "mac_os"].includes(device?.os?.toLowerCase() ?? ""),
        [device?.os]
    );
    let titleTranslationLabel = "SetupSecurityQuestionPrompt";
    let securityQuestionSetupInstructionLabel = "SecurityQuestionSetupInstruction";
    if (resSelectedSecurityQuestionId) {
        titleTranslationLabel = "VerifyIdentityPrompt";
        securityQuestionSetupInstructionLabel = "AnswerSecurityQuestionInstruction";
    }

    // Memoize translations
    const translations = useMemo(() => ({
        title: translate(questionnaire, titleTranslationLabel, language),
        instruction: translate(questionnaire, securityQuestionSetupInstructionLabel, language),
        selectLabel: translate(questionnaire, "SelectSecurityQuestionLabel", language),
        errorMessage: translate(questionnaire, "IncompleteAnswer", language),
        invalidAnswer: translate(questionnaire, "SecurityAnswerIncorrect", language),
        LockedSurveyAccessTitle: translate(questionnaire, "AccessTemporarilyLocked", language),
        LockedSurveyAccessInstruction: translate(questionnaire, "SecurityRecoveryMessage", language),
    }), [questionnaire, language]);
    // Memoize security questions list
    const { options, emptyAnswerOption } = useMemo(() => {
        const securityQuestionList: SecurityQuestionModel[] = translatedSecurityQuestionsList(questionnaire.securityQuestionsList, language);
        const options: Option[] = securityQuestionList.map((item: SecurityQuestionModel) => {
            if (isReAccess && item.id === resSelectedSecurityQuestionId) {
                setResSelectedQuestionText(item.questionText);
                setSelectedQuestion(resSelectedSecurityQuestionId.toString());
            }

            return {
                id: item.id.toString(),
                label: item.questionText,
                value: item.questionId.toString()
            };
        });

        const emptyAnswerOption: Option = {
            id: "",
            label: translations.selectLabel,
            value: "",
            isNonInteractive: true
        };

        return { options, emptyAnswerOption };
    }, [questionnaire.securityQuestionsList, language, translations.selectLabel]);


    // Memoize dummy question
    const dummyQuestion = useMemo<QuestionModel>(() => ({
        elementId: "security_question_dropdown",
        name: "Security Question",
        type: QuestionType.TextAnswer,
        id: 2,
        index: 0,
        blockIndex: 0,
        isPageBreak: false,
        variables: [],
        variableNames: [],
        variableDependencies: [],
        settings: {}
    }), []);

    useEffect(() => {
        setIsLocked(isResLocked);
        if (isLocked) {
            setSaveError(translations.LockedSurveyAccessTitle);
        }
    }, [isResLocked, isLocked, translations.LockedSurveyAccessTitle]);

    const onSelectedChanged = useCallback((values: string[]) => {
        const [value] = values;
        setSelectedQuestion(value);
        if (validationState.questionError) {
            setValidationState(prev => ({ ...prev, questionError: undefined }));
        }
    }, [validationState.questionError]);

    const onAnswerChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setAnswer(e.target.value);
        if (validationState.answerError) {
            setValidationState(prev => ({ ...prev, answerError: undefined }));
        }
    }, [validationState.answerError]);

    const validateForm = useCallback((): boolean => {
        const newValidationState: ValidationState = {};
        let isValid = true;

        if (!selectedQuestion) {
            newValidationState.questionError = {
                key: "RequiredField",
                message: translations.errorMessage
            };
            isValid = false;
        }

        if (!answer.trim()) {
            newValidationState.answerError = {
                key: "RequiredField",
                message: translations.errorMessage
            };
            isValid = false;
        }

        setValidationState(newValidationState);
        return isValid;
    }, [selectedQuestion, answer, translations.errorMessage]);

    const handleSubmit = useCallback(async () => {
        if (!validateForm() || isSubmitting) return;

        try {
            setIsSubmitting(true);
            setSaveError(null);

            const result = await dispatch(saveSecurityQuestionInfo(selectedQuestion, answer));

            // Only update state if component is still mounted
            if (!mountedRef.current) return;

            if (result.isSuccessful) {
                await onNavigation(NavigationType.Submit);
            } else {
                if (result.errorMessage == "Invalid Answer") {
                    if (result.isLocked) {
                        setSaveError(translations.LockedSurveyAccessTitle);
                        onLockedChange(true);
                        setIsLocked(result.isLocked);
                    } else {
                        setSaveError(translations.invalidAnswer);
                    }
                } else {
                    setSaveError(result.errorMessage);
                }
            }
        } catch (error) {
            // Only update state if component is still mounted
            if (mountedRef.current) {
                console.error('Error in handleSubmit:', error);
                setSaveError(translations.errorMessage);
            }
        } finally {
            // Only update state if component is still mounted
            if (mountedRef.current) {
                setIsSubmitting(false);
            }
        }
    }, [validateForm, onNavigation, selectedQuestion, answer, dispatch, translations.invalidAnswer, isSubmitting]);

    // Update the submit handler for the window
    useEffect(() => {
        const submitHandler = async () => {
            if (!isSubmitting) {
                await handleSubmit();
            }
        };

        // @ts-ignore - Adding custom property to window for parent component
        window.handleSecurityQuestionSubmit = submitHandler;
        return () => {
            // @ts-ignore
            delete window.handleSecurityQuestionSubmit;
        };
    }, [handleSubmit, isSubmitting]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const inputHandlers = useMemo(() => ({
        onBlur: () => setFocused(false),
        onFocus: () => setFocused(true)
    }), []);

    const isNotValid = validationState.questionError || validationState.answerError || saveError;

    const { elementReference, onKeyDown } = useKeyboardNavigation(
        `${dummyQuestion.elementId}_INPUT`,
        { id: dummyQuestion.elementId },
        dummyQuestion,
        TextAnswerFieldNavigation,
        null,
    );

    return (
        <div
            className={classNames(
                classes.securityQuestionContainer,
                isNotValid && useModernTheme && classes.securityQuestionError,
            )}
        >
            {isNotValid && (
                <div className={classes.questionValidation}>
                    <ValidationError
                        question={dummyQuestion}
                        questionnaire={questionnaire}
                        language={language}
                        error={saveError ? { key: "InvalidSecurityAnswer", message: saveError } : validationState.questionError || validationState.answerError}
                    />
                </div>
            )}
            {
                !isLocked ? (
                    <div className={classNames(
                        isNotValid && !useModernTheme && classes.questionValidationFocus,
                        isNotValid && !useModernTheme && isDisplayWide && classes.dynamicQuestionValidationFocus,
                    )}>
                        <h2 id="SEC_QUESTION_TITLE" className={classes.secQuestionTitle}>
                            {translations.title}
                        </h2>
                        <div className={classes.secQuestionText}>
                            <QuestionText text={translations.instruction} isIOS={isIOS} />
                        </div>
                        {
                            isReAccess ? (
                                <h2 className={classes.secQuestionTitle}>
                                    {resSelectedQuestionText}
                                </h2>) : (
                                <div className={classes.questionContainer}>
                                    <Select
                                        id="SECURITY_QUESTION__DROP_DOWN"
                                        labelId="SECURITY_QUESTION_SELECTOR_LABEL"
                                        value={[selectedQuestion]}
                                        onChange={onSelectedChanged}
                                        size="Half"
                                        options={[emptyAnswerOption, ...options]}
                                        variable={{
                                            ...dummyQuestion,
                                            maxMention: 1,
                                            choiceList: [],
                                            dataType: VariableDataType.Text,
                                            type: VariableType.Computed
                                        }}
                                    />
                                </div>
                            )
                        }
                        {selectedQuestion && (
                            <div className={classes.answerContainer}>
                                <SecurityQuestionInput
                                    answer={answer}
                                    onChange={onAnswerChange}
                                    onFocus={inputHandlers.onFocus}
                                    onBlur={inputHandlers.onBlur}
                                    focused={focused}
                                    hasError={!!validationState.answerError}
                                    className={classes.textAnswer}
                                    onKeyDown={onKeyDown}
                                    elementReference={elementReference}
                                    submitSecurityQuestionForm={handleSubmit}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        {translations.LockedSurveyAccessInstruction}
                    </div>
                )
            }
        </div>
    );
}